import { io, Socket } from 'socket.io-client';
import { logger } from '../utils/logger';

const WS_URL = import.meta.env.VITE_WS_URL || import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:3001';

export type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface ChatClientConfig {
  getToken: () => Promise<string | null>;
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  onConnectionStateChange?: (state: ConnectionState) => void;
}

/**
 * WebSocket client for chat functionality
 * Handles connection, reconnection, and event management
 */
export class ChatWebSocketClient {
  private socket: Socket | null = null;
  private config: ChatClientConfig;
  private connectionState: ConnectionState = 'disconnected';
  private isManualDisconnect = false;

  constructor(config: ChatClientConfig) {
    this.config = config;
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.socket?.connected) {
      return;
    }

    if (this.socket) {
      this.disconnect();
    }

    this.isManualDisconnect = false;
    this.updateConnectionState('connecting');

    try {
      const token = await this.config.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      this.socket = io(WS_URL, {
        auth: {
          token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true, // Enable automatic reconnection
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity, // Keep trying to reconnect
        timeout: 45000,
        forceNew: false, // Reuse connection if available
        autoConnect: true,
      });

      this.setupEventHandlers();
    } catch (error) {
        const err = error as Error;
        logger.error('Failed to connect WebSocket', err);
        this.updateConnectionState('error');
        this.config.onError?.(err);
        // Socket.io will handle reconnection automatically
      }
  }

  /**
   * Setup event handlers for the socket
   */
  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      logger.info('WebSocket connected', { socketId: this.socket?.id });
      this.updateConnectionState('connected');
      this.config.onConnect?.();
    });

    this.socket.on('disconnect', (reason: string) => {
      logger.info('WebSocket disconnected', { reason });
      this.updateConnectionState('disconnected');
      this.config.onDisconnect?.(reason);

      // Socket.io handles reconnection automatically when reconnection: true
      // Only prevent reconnection if manually disconnected
      if (this.isManualDisconnect) {
        this.socket?.disconnect();
      }
    });

    this.socket.on('connect_error', (error: Error) => {
      logger.error('WebSocket connection error', error);
      this.updateConnectionState('error');
      this.config.onError?.(error);
      // Socket.io will automatically retry when reconnection: true
      // Don't manually schedule reconnect
    });

    this.socket.on('error', (error: any) => {
      logger.error('WebSocket error', error);
      this.config.onError?.(error);
    });
  }


  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isManualDisconnect = true;

    if (this.socket) {
      // Disable reconnection before disconnecting
      this.socket.io.opts.reconnection = false;
      this.socket.disconnect();
      this.socket = null;
    }

    this.updateConnectionState('disconnected');
  }

  /**
   * Get the socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * Update connection state and notify listeners
   */
  private updateConnectionState(state: ConnectionState): void {
    if (this.connectionState !== state) {
      this.connectionState = state;
      this.config.onConnectionStateChange?.(state);
    }
  }

  /**
   * Emit an event to the server
   */
  emit(event: string, data: any): void {
    if (!this.socket?.connected) {
      logger.warn('Cannot emit event: socket not connected', { event });
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Subscribe to an event
   */
  on(event: string, callback: (...args: any[]) => void): void {
    if (!this.socket) {
      logger.warn('Cannot subscribe to event: socket not initialized', { event });
      return;
    }

    this.socket.on(event, callback);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) {
      return;
    }

    this.socket.off(event, callback);
  }

  /**
   * Subscribe to an event once
   */
  once(event: string, callback: (...args: any[]) => void): void {
    if (!this.socket) {
      logger.warn('Cannot subscribe to event: socket not initialized', { event });
      return;
    }

    this.socket.once(event, callback);
  }
}
